import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import StudentsTable from '@/tables/coordinator/StudentsTable';
import MyStudentsTable from '@/tables/MyStudentsTable';
import { useIsCoordinatorGroup } from '~/hooks/useIsCoordinatorGroup';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/all-students/',
)({
  // L23 — the Study Helpers tab's drill-in. Legacy carries it in the URL
  // FRAGMENT (`Columns/Users/StudentAdopted.php:29`), which is never sent to a
  // server; a validated search param is the same behaviour in the shape the
  // router already checks, and it closes the unwhitelisted-key hole the legacy
  // reader has (`wbs-datatable.js:580-597` applies any `key=value` in the hash,
  // including the unrendered `admin_any`). W3.9 X28.
  validateSearch: (search): { study_helper_id?: number } => {
    const id = Number(search.study_helper_id);
    return {
      study_helper_id: Number.isInteger(id) && id > 0 ? id : undefined,
    };
  },
  component: StudentsScreen,
});

function StudentsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { study_helper_id: deepLinkedHelperId } = Route.useSearch();
  const navigate = useNavigate();

  // Read ONCE and then cleared, which is exactly what legacy does with the
  // hash: `wbs-datatable.js:583-594` writes the pairs into `controls.filters`
  // and immediately sets `window.location.hash = ''`, and its loop that would
  // have restored them into the panel is commented out (`:16-51`). So the first
  // request is filtered, the URL stops carrying it, and the panel owns the value
  // from its first Apply/Reset onward — including when the user clears the Study
  // Helper control, which `useWbsTableQuery`'s `seedParams` is what makes true
  // (a `panel ?? seed` fallback re-arms the deep link on an emptied control).
  // `replace: true` keeps the strip out of the history stack, so Back leaves the
  // screen instead of re-arming the filter.
  const [seededHelperId] = useState(() =>
    deepLinkedHelperId ? [deepLinkedHelperId] : undefined,
  );
  useEffect(() => {
    if (deepLinkedHelperId === undefined) return;
    void navigate({
      to: '/groups/$groupName/students/all-students/',
      params: { groupName },
      search: { study_helper_id: undefined },
      replace: true,
    });
  }, [deepLinkedHelperId, groupName, navigate]);

  // Group type alone: `all-students.php:34-69` dispatches on `$group_type` and
  // never reads the viewer's role, so the role-and-type `useIsCoordinator` put a
  // platform admin (whose `viewer_role` is null on a group they hold no row in)
  // on the wrong table for the group's type.
  const isCoordinatorGroup = useIsCoordinatorGroup();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: {
      group_slug: groupName,
    },
  });
  const groupId = groupData?.id || 0;

  const handleAddPostalStudent = () => {
    navigate({
      to: '/groups/$groupName/students/all-students/new/',
      params: { groupName },
    });
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.students')} />
        {/* Legacy offers this only for the `study-groups` alias
            (`all-students.php:25-29`), which `WBS_Group_Types.php:48-51` expands
            to the study group plus the folded-away explorer group — never a
            congregation, a custom study group or a follow-up group. The old
            `!isCoordinator` guard handed it to all three. */}
        {groupData?.group_type === 'STUDY_HELPER_STUDY_GROUP' && (
          <WBSButton wide onClick={handleAddPostalStudent}>
            {t('actions.add-postal-student')}
          </WBSButton>
        )}
      </GroupHeaderWrapper>
      {isCoordinatorGroup ? (
        <StudentsTable
          groupId={groupId}
          groupType={groupData?.group_type ?? undefined}
          seededHelperId={seededHelperId}
        />
      ) : (
        // No `seededHelperId`: `study-group-students` mounts no Study Helper
        // control (`StudyGroups/Students.php:19-29`) and the Study Helpers tab
        // that emits the link is congregation/custom-study-group only, so the
        // deep link cannot land here.
        <MyStudentsTable groupId={groupId} />
      )}
    </>
  );
}
